import { z } from "zod";

// ---------------------------------------------------------------------------
// 継続案件（#289、docs/101 §5〜§8、2026-11-02 射場さん ★A〜C）。
//   固定の得意先に見積書・{deliveryNote}・請求書を積む{project}（材料屋＝現場、営業力＝契約）。project_groups.is_ongoing。
//   会社の使う機能「{ongoingProject}」（ongoingProjects、既定オフ・業種で既定）がオンのときだけ画面に出す（データは残る）。
//   作成画面の{project}の欄の既定＝その{customer}の完了していない継続案件が1つならそれ・複数なら最後に使ったもの・無ければ「新規（{subject}と同じ）」
//   （DB の fn_default_project）。継続⇄通常はいつでも（★A）・完了した継続案件にも書類は作れる（警告だけ＝★B）
// ---------------------------------------------------------------------------

/**
 * 書類の作成の{project}の選び方（POST /estimates・/delivery-notes の projectId。docs/101 §10 の projectId＝今の projectGroupId と同じ{project}）：
 *   {project}の id／"new"＝新規（{subject}と同じ名前の{project}を一緒に作る。今までの newGroup）／"ongoing:new"＝{customer}の略称＋{ongoingProject}の継続案件を作る
 */
export const ProjectChoice = z.union([z.literal("new"), z.literal("ongoing:new"), z.string().uuid()]);
export type ProjectChoice = z.infer<typeof ProjectChoice>;

/** 書類の作成の答えに付く警告（完了した継続案件に作ったとき。★B＝止めない） */
export const CreateWarning = z.object({ warning: z.string().optional() });
export type CreateWarning = z.infer<typeof CreateWarning>;

/** 作成画面の{project}の欄の候補の1つ（GET /groups?partner=… の行を絞ったもの） */
export const GroupChoiceItem = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isOngoing: z.boolean(),
  completedAt: z.string().nullable(),
  /** 最後に書類を作った日（「最終 10/30」）。無ければ null */
  lastUsedOn: z.string().nullable(),
  partnerBranchId: z.string().uuid().nullable(),
  partnerContactId: z.string().uuid().nullable(),
  staffName: z.string(),
  quoteCount: z.number().int(),
});
export type GroupChoiceItem = z.infer<typeof GroupChoiceItem>;

/** GET /groups/default?partner=（docs/101 §6）。choice＝その id か new。会社の継続案件がオフなら必ず new */
export const GroupDefault = z.object({
  choice: z.union([z.literal("new"), z.string().uuid()]),
  group: GroupChoiceItem.nullable(),
});
export type GroupDefault = z.infer<typeof GroupDefault>;

/** 月別の1行（GET /groups/:id/monthly。v_project_monthly）。margin は原価が1件も無い月は null（ルール7＝「—」） */
export const GroupMonthlyRow = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  sales: z.number().int(),
  cost: z.number().int(),
  margin: z.number().int().nullable(),
  unbilled: z.number().int(),
});
export type GroupMonthlyRow = z.infer<typeof GroupMonthlyRow>;
export const GroupMonthly = z.object({ items: z.array(GroupMonthlyRow) });
export type GroupMonthly = z.infer<typeof GroupMonthly>;

/** 継続案件の名前の既定（docs/101 §5）：{customer}の略称＋{ongoingProject}（「見本商事 現場」「○○社 契約」） */
export function ongoingNameOf(shortName: string, ongoingProject: string): string {
  return `${shortName.trim()} ${ongoingProject.trim()}`.trim().slice(0, 200);
}

/** 月の初めの日（YYYY-MM-01）から「2026年10月」 */
export const monthLabelOf = (ym: string): string => `${Number(ym.slice(0, 4))}年${Number(ym.slice(5, 7))}月`;

/**
 * 画面の文（docs/10 §5.1.2＝題・見出し・ボタンは名詞だけ）。{ongoingShort} は ongoingShortOf（「継続案件」→「継続」）を画面が入れる。
 * 「終了」は使わない（{project}の今の言葉「完了」。docs/101 v0.4）
 */
export const ONGOING_MSG = {
  /** 作成画面の{project}の欄 */
  field: "{project}",
  /** 欄の選択肢 */
  pickOngoing: "{ongoingProject}",
  pickActive: "進行中の{project}",
  pickNew: "新規（{subject}と同じ）",
  pickCreateOngoing: "{ongoingProject}作成",
  /** 欄に入った「新しい継続案件」（作成で名前が決まる） */
  newOngoingValue: "{name}（新規）",
  lastUsed: (md: string) => `最終 ${md}`,
  activeCount: (n: number) => `${n}件`,
  /** 完了した継続案件に作ったとき（★B）。{name}＝{project}の名前 */
  completedWarning: "完了した{project}「{name}」に作成しました",
  completedChip: "完了",
  /** 新しい{project}（GroupNew） */
  ongoingSwitch: "{ongoingShort}",
  startedOn: "開始日",
  /** 一覧 */
  tabActive: "進行中",
  tabDone: "完了",
  tabAll: "すべて",
  monthSales: "今月売上",
  unbilled: "未請求",
  started: (ymd: string) => `開始 ${ymd.replaceAll("-", "/")}`,
  /** 行の ⋯ */
  rename: "名称変更",
  toggleOngoing: "継続切替",
  complete: "完了",
  reopen: "再開",
  remove: "削除",
  renameTitle: "名称変更",
  renamed: "{project}の名前を「{name}」にしました",
  toOngoing: "「{name}」を{ongoingProject}にしました",
  toRegular: "「{name}」を通常の{project}にしました",
  /** 一覧の行の「削除」の確かめ（ゴミ箱の見積書が無いとき。あるときは GROUP_MSG.deleteWithTrash） */
  deleteAsk: "{project}「{name}」を削除します",
  /** {project}の画面の月別の表 */
  monthlyTitle: "月別",
  monthCol: "月",
  salesCol: "売上",
  costCol: "原価",
  marginCol: "粗利",
  unbilledCol: "未請求",
  monthlyNone: "まだ売上・原価はありません",
} as const;
